import React, {useMemo, useRef} from 'react';
import {OverlayPanel} from "primereact/overlaypanel";
import {ListBox} from "primereact/listbox";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import Icon from "../../../system/components/icon";
import {useTranslate} from "../../../system/useTranslate";

function MultiSelectProjectsDeliverables({
                                             value = [],
                                             onChange,
                                             options = []
                                         }) {
    const overlayRef = useRef(null);
    const translate = useTranslate();

    const selectedIds = useMemo(
        () => (value ?? []).map(item => item.id),
        [value]
    );

    const selectedProjectIds = useMemo(
        () => new Set((value ?? []).map(item => item?.project?.id).filter(Boolean)),
        [value]
    );

    const listOptions = useMemo(() => {
        return (options ?? []).flatMap(project =>
            (project?.deliverables ?? []).map(deliverable => {
                const checked = selectedIds.includes(deliverable.id);

                const disabled =
                    selectedProjectIds.has(project.id) && !checked;

                return {
                    label: deliverable?.title || deliverable?.description || `#${deliverable?.id}`,
                    value: deliverable.id,
                    data: {
                        ...deliverable,
                        project
                    },
                    projectTitle: project?.title,
                    disabled
                };
            })
        );
    }, [options, selectedIds, selectedProjectIds]);

    const selectedOptions = useMemo(() => {
        return listOptions.filter(option => selectedIds.includes(option.value));
    }, [listOptions, selectedIds]);

    function SelectedItemTemplate(option) {
        return (
            <div className="w-fit flex align-items-center gap-1 text-xs p-1 px-2 surface-100 border-round-2xl">
                <Icon icon="mi mi:circle" className="text-3xs"/>
                <span>{option.label}</span>
            </div>
        );
    }

    function ItemTemplate(option) {
        return (
            <div className="flex align-items-center justify-content-between w-full">
                <div className="flex flex-column">
                    <span className="text-xs">{option.label}</span>
                    <span className="text-2xs text-400">{option.projectTitle}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex align-items-center gap-1 flex-wrap">
            <Button
                type="button"
                text
                severity="secondary"
                aria-label={translate("select-deliverable")}
                className="cursor-pointer border-none bg-transparent p-0 shadow-none outline-none"
                style={{outline: "none", boxShadow: "none"}}
                onClick={(e) => overlayRef.current?.toggle(e)}
            >
                <Icon
                    icon="mi mi:add_circle"
                    className="text-lg"
                />
            </Button>

            {selectedOptions.map(option => (
                <SelectedItemTemplate
                    key={option.value}
                    {...option}
                />
            ))}

            <OverlayPanel
                ref={overlayRef}
                pt={{
                    root: () => ({
                        className: "p-0 rtl w-15rem project-overlay"
                    }),
                    content: () => ({
                        className: "p-0"
                    })
                }}
            >
                <ListBox
                    className="w-15rem border-none"
                    multiple
                    checkbox
                    filter
                    filterBy="label,projectTitle"
                    options={listOptions}
                    value={selectedIds}
                    itemTemplate={ItemTemplate}
                    pt={{
                        list: () => ({
                            className: "max-h-15rem overflow-y-auto"
                        }),
                        item: () => ({
                            className: "p-2"
                        }),
                        header: () => ({
                            className: "p-2 sticky top-0 surface-overlay z-1"
                        })
                    }}
                    filterTemplate={(options) => (
                        <div className="p-2 surface-50">
                            <InputText
                                className=" w-full text-xs"
                                placeholder={translate("search-deliverables-placeholder")}
                                onChange={options.filterOptions.filter}
                            />
                        </div>
                    )}
                    onChange={(e) => {
                        const nextDeliverables = listOptions
                            .filter(option => e.value.includes(option.value))
                            .map(option => option.data);

                        onChange?.(nextDeliverables);
                    }}
                />
            </OverlayPanel>
        </div>
    );
}

export default MultiSelectProjectsDeliverables;
